import type { CSSProperties, ReactElement } from "react";
import { useSofortPopup } from "@kundeninfo/ui";
import { kuerzelVon } from "./ticketFormat.js";

/**
 * DER ZUSTÄNDIGE ALS AVATAR (F48, Vorbild GitLab): Kreis mit Initialen in einer stabilen
 * Nutzerfarbe (deterministisch aus der Kennung, Palette aus me-design-Tokens) plus voller Name.
 * Eigene Zuständigkeit mit Rand in Aktionsfarbe, niemand = gestrichelter Kreis. Hover/Tap zeigt
 * den Steckbrief (Name · Kürzel · Mail) als Sofort-Popup. Übergang T-5: me-design hat kein
 * Avatar-Element — diese EINE Datei wird ersetzt, sobald es eines gibt (F44-Mechanik). Der
 * Bild-Slot steht schon; den Profilbild-Upload entscheidet Dirk (137).
 */

export const AVATAR_FARBEN = [
  "--medium-strom", "--medium-gas", "--medium-waerme", "--medium-wasser",
  "--auswahl1", "--auswahl2", "--marke", "--status-laeuft",
] as const;

export function farbIndex(id: string): number {
  let h = 0;
  for (const z of id) h = (h * 31 + z.charCodeAt(0)) >>> 0;
  return h % AVATAR_FARBEN.length;
}

export type AvatarPerson = { id: string; name: string; email?: string | null; bild?: string | null };

/** Ohne sichtbaren Namen trägt der Avatar den Steckbrief als zugänglichen Namen — sonst bliebe für
 *  die Vorlesestimme nur ein stummer Kreis (Akte-Liste, Review 19). */
function namenlos(ohneName: boolean | undefined, steckbrief: string): Record<string, string> {
  return ohneName === true ? { role: "img", "aria-label": steckbrief } : {};
}

export function Avatar(p: { person: AvatarPerson | null; ich?: boolean; ohneName?: boolean }): ReactElement {
  const steckbrief = p.person === null ? "nicht zugewiesen"
    : [p.person.name, kuerzelVon(p.person.name), p.person.email ?? ""].filter((x) => x !== "").join(" · ");
  const ref = useSofortPopup<HTMLSpanElement>(steckbrief);
  if (p.person === null) {
    return (
      <span ref={ref} className="av av--niemand" tabIndex={0} {...namenlos(p.ohneName, steckbrief)}>
        <span className="av__kreis" aria-hidden="true" />
        {p.ohneName !== true && <span className="av__name">nicht zugewiesen</span>}
      </span>
    );
  }
  const stil = { "--av-farbe": `var(${AVATAR_FARBEN[farbIndex(p.person.id)]})` } as CSSProperties;
  return (
    <span ref={ref} className={`av${p.ich === true ? " av--eigen" : ""}`} tabIndex={0}
      {...namenlos(p.ohneName, steckbrief)}>
      <span className="av__kreis" aria-hidden="true" style={stil}>
        {p.person.bild !== undefined && p.person.bild !== null
          ? <img className="av__bild" src={p.person.bild} alt="" />
          : kuerzelVon(p.person.name)}
      </span>
      {p.ohneName !== true && <span className="av__name">{p.person.name}</span>}
    </span>
  );
}
